import {Component, OnInit, OnDestroy, HostListener, NgZone, ChangeDetectorRef} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Sidenavaction} from '../../../../assets/js/application/sidenavaction';
import {BusinessEntityService} from '../../../services/businessEntity.service';
import BusinessEntity, {Address} from '../../../services/businessEntity.model';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {FarmSiteService} from '../../../services/farmSite.service';
import FarmSiteModel, {
  BioSecurity,
  Contract,
  IWaterType,
  ISoilType,
  IAddress,
  SMEContract,
  BuyerContract
} from '../../../services/farmSite.model';
import {WaterTypeService} from '../../../services/waterType.service';
import WaterTypeModel from '../../../services/waterType.model';
import {SoilTypeService} from '../../../services/soilType.service';
import SoilTypeModel from '../../../services/soilTypeModel';
import * as _ from 'lodash';
import moment from 'moment';
import {CustomerPondService} from '../../../services/customerPond.service';
import CustomerPondModel from '../../../services/customerPond.model';
import PersonsModel from '../../../services/persons.model';
import {PersonsService} from '../../../services/persons.service';
import ImageDetailsModel from 'src/app/services/images-details.model';
import {ImageDetailsService} from 'src/app/services/images-details.service';
import { BusinessTypeService } from 'src/app/services/businessType.service';
import BusinessTypeModel from 'src/app/services/businessType.model';
import {ToastrService} from 'ngx-toastr';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/services/previousRouteService';
import { CountriesAPIService } from 'src/app/services/countries.service';
import { StatesAPIService } from 'src/app/services/states.service';
import { StateModel, VillageModel } from 'src/app/services/json.model';
import CountriesModel from 'src/app/services/countries.model';
import {BatchLoader} from "../../../Utils/batch-loader.util";

declare var $: any;

/**
 * @component LocationComponent
 * @selector app-business-entity-farms
 * @implements {OnInit, OnDestroy}
 *
 * @description
 * The `LocationComponent` manages the creation, updating, and listing of Farm Sites associated with a Business Entity.
 * It serves as a comprehensive form and dashboard for managing farm infrastructure details, contracts, and location data.
 *
 * ## Key Responsibilities:
 * 1. **Farm Site Management**: Allows users to create new farm sites or update existing ones with details like name, area, and year started.
 * 2. **Location Management**: Handles hierarchical location selection (Country -> State -> Village) and geocoding (Pincode -> Lat/Long).
 * 3. **Contract Management**: Manages relationships and contracts with Feed Providers, SMEs, and Buyers.
 * 4. **Bio-Security Configuration**: Allows configuration of bio-security measures (Fencing, Reservoirs, etc.).
 * 5. **Water & Soil Details**: Captures environmental parameters for the farm site.
 *
 * ## User Roles & Permissions:
 * - **Business Admin (Provider)**: Can manage farm sites for their client farms.
 * - **Farmer / Farm Manager**: Can manage their own farm sites.
 * - **Admin**: System-wide access.
 *
 * ## LocalStorage Dependencies:
 * - `loginPersonType`: User role.
 * - `loginBusinessCategory`: Business context.
 * - `businessID`: Logged-in business ID.
 * - `personID`: Logged-in user ID.
 * - `loginBusinessIcon`: Cached business icon path.
 *
 * @service BusinessEntityService - For business entity operations.
 * @service FarmSiteService - For farm site CRUD operations.
 * @service WaterTypeService - For fetching water types.
 * @service SoilTypeService - For fetching soil types.
 * @service PersonsService - For fetching associated persons (SMEs, Buyers).
 * @service CountriesAPIService - For country data.
 * @service StatesAPIService - For state and village data.
 */
@Component({
  selector: 'app-locations',
  templateUrl: './locations.component.html',
  styleUrls: ['./locations.component.scss']
})
export class LocationComponent implements OnInit, OnDestroy {
  /** @type {BusinessEntity} The main business entity model. */
  public businessEntity: BusinessEntity;

  /** @type {Array<BusinessEntity>} List of business entities. */
  public businessEntities: Array<BusinessEntity>;

  /** @type {Array<BusinessTypeModel>} List of business types. */
  public businessTypes: Array<BusinessTypeModel>;

  /** @type {Array<FarmSiteModel>} List of farm sites. */
  public farmSites: Array<FarmSiteModel>;

  /** @type {FarmSiteModel[]} List of farm sites (duplicate/alias). */
  public farmSitesList: FarmSiteModel[] = [];

  /** @type {FarmSiteModel} Current farm site model being created/edited. */
  public farmSite: FarmSiteModel = new FarmSiteModel();

  /** @type {FarmSiteModel[]} Filtered list of farm sites for display. */
  public filteredFarmSites: FarmSiteModel[] = [];

  /** @type {BioSecurity} Bio-security configuration model. */
  public bioSecurity: BioSecurity;

  /** @type {Contract} Contract model for feed providers. */
  public contract: Contract;

  /** @type {SMEContract} Contract model for SMEs. */
  public smeContract: SMEContract;

  /** @type {BuyerContract} Contract model for Buyers. */
  public buyerContract: BuyerContract;

  /** @type {IWaterType} Water type configuration. */
  public waterType: IWaterType;

  /** @type {ISoilType} Soil type configuration. */
  public soilType: ISoilType;

  /** @type {IAddress} Address model for the farm site. */
  public address: IAddress;

  /** @type {Array<WaterTypeModel>} List of available water types. */
  public waterTypes: Array<WaterTypeModel>;

  /** @type {Array<SoilTypeModel>} List of available soil types. */
  public soilTypes: Array<SoilTypeModel>;

  /** @type {Array<CustomerPondModel>} List of customer ponds. */
  public customerPonds: Array<CustomerPondModel>;

  /** @type {Array<PersonsModel>} List of persons (generic). */
  public persons: Array<PersonsModel>;

  /** @type {Array<PersonsModel>} List of buyer persons. */
  public buyerPersons: Array<PersonsModel>;

  /** @type {Array<PersonsModel>} List of persons associated with business. */
  public personsByBusinessId: Array<PersonsModel>;

  /** @type {PersonsModel} Single person model. */
  public person: PersonsModel;

  /** @type {Array<ImageDetailsModel>} List of image details. */
  public imageDetails: Array<ImageDetailsModel>;

  /** @type {ImageDetailsModel} Single image detail model. */
  public imageDetail: ImageDetailsModel;

  /** @type {string} Business ID from route parameters. */
  public paramsbusinessId: string;

  /** @type {string} Navigation parameter for business ID. */
  public navparamBusinessID: string;

  /** @type {boolean} Page loading state flag. */
  public pgLoad = false;

  /** @type {string} Logged-in user role. */
  public loginPersonType: string;

  /** @type {FormGroup} Reactive form for adding/editing farm sites. */
  public addFarmsiteForm: FormGroup;

  /** @type {boolean} Flag indicating if form is submitted. */
  public submitted = false;

  /** @type {boolean} Flag indicating if pond form is submitted. */
  public pondSubmitted = false;

  /** @type {string} ID of the farm business. */
  public farmbusinessid: string;

  /** @type {Array<any>} Array of years for 'Year Started' dropdown. */
  public yearStartedArr = new Array();

  /** @type {boolean} Flag for SME contract selection. */
  public isSMEChecked: boolean;

  /** @type {boolean} Flag for Buyer contract selection. */
  public isBuyerChecked: boolean;

  /** @type {boolean} Validation flag for water spread area. */
  public waterareavalidation: boolean;

  /** @type {Array<any>} Array of month names. */
  public strArray = new Array();

  /** @type {Array<any>} List of villages. */
  public villagesList = new Array();

  /** @type {boolean} Flag to display postal info. */
  public isPostalInfoDisp = true;

  /** @type {boolean} Flag indicating invalid pincode. */
  public isPinNotValid = false;

  /** @type {boolean} Bio-security: Fencing. */
  public bioFencing: boolean;

  /** @type {boolean} Bio-security: Hand Wash & Footbath. */
  public bioHWandFA: boolean;

  /** @type {boolean} Bio-security: Crab Fencing. */
  public bioCrabFencing: boolean;

  /** @type {boolean} Bio-security: Reservoirs. */
  public bioReservoirs: boolean;

  /** @type {boolean} Bio-security: Bird Fencing. */
  public bioBirdFencing: boolean;

  /** @type {boolean} Bio-security: Bio Filters. */
  public bioFilters: boolean;

  /** @type {boolean} Flag to show submit button. */
  public submitButton: boolean;

  /** @type {boolean} Flag to show update button. */
  public updateButton: boolean;

  /** @type {string} Logged-in business ID. */
  public loginBusinessID: string;

  /** @type {string} Logged-in business category. */
  public loginUserBusinessCategory: string;

  // ************Update Farm site declarations **************
  /** @type {string} Selected year started. */
  public selectedYear: string;

  /** @type {Array<string>} Array of pond IDs for deletion. */
  public pondArr: Array<string>;

  /** @type {string} Selected data type for deletion. */
  public selectedDeletedataType: string;

  /** @type {string} Country code. */
  public countryCode: string;

  /** @type {BusinessEntity} Pincode validation result. */
  public pinCodes: BusinessEntity;

  /** @type {Array<BusinessEntity>} List of SME businesses. */
  public smeBusinessEntities: Array<BusinessEntity>;

  /** @type {Array<BusinessEntity>} List of Buyer businesses. */
  public buyerBusinessEntities: Array<BusinessEntity>;

  /** @type {Array<PersonsModel>} List of provider persons. */
  public providerPersons: Array<PersonsModel>;

  /** @type {any} Business parameters. */
  public paramsBusiness: any;

  /** @type {any} Application permissions. */
  public appPermission: any;

  /** @type {boolean} Validation flag for grow-out ponds. */
  public growOutPondsValidation: boolean;

  /** @type {boolean} Validation flag for grow-out ponds digits. */
  public growOutPondsDigits: boolean;

  /** @type {FarmSiteModel} Lat/Long data. */
  public latLongs: FarmSiteModel;

  /** @type {Array<any>} List of countries. */
  public countries = new Array();

  /** @type {Array<StateModel>} List of states. */
  public states: Array<StateModel>;

  /** @type {Array<VillageModel>} List of villages. */
  public villages: Array<VillageModel>;

  /** @type {any} Generic item. */
  public item: any;

  /** @type {any} Selected state item. */
  public stateitem: any;

  /** @type {any} Selected village item. */
  public villageItem: any;

  /** @type {string} Search keyword. */
  public keyword: string;

  /** @type {string} State search keyword. */
  public statekeyword: string;

  /** @type {string} Village search keyword. */
  public villagekeyword: string;

  /** @type {string} Selected country name. */
  public country: string;

  /** @type {boolean} Flag indicating if village changed. */
  public villageChanged: boolean = false;

  /** @type {boolean} Flag to disable state/village fields. */
  public stateVillageDisabled: boolean = false;

  /** @type {boolean} Flag to prevent double clicks. */
  public locationCreationDoubleClick: boolean;

  /** @type {Array<BusinessEntity>} List of provider businesses. */
  public providerBusinessEntities: Array<BusinessEntity>;

  /** @type {Array<PersonsModel>} List of persons by provider business. */
  public personsByProviderBusinessId: Array<PersonsModel>;

  /** @type {boolean} Flag for Provider vs SME selection. */
  public providerOrSme: boolean;

  /** @type {Array<any>} Provider chat user config. */
  public providerChatUser = new Array();

  /** @type {string | ArrayBuffer} Login user business icon. */
  public loginUserBusinessIcon: string | ArrayBuffer;

  /** @type {boolean} Flag for country dropdown. */
  public isCountryDropdown = false;

  /** @type {boolean} Flag for state dropdown. */
  public isStateDropdown = false;

  /** @type {boolean} Flag for village dropdown. */
  public isVillageDropdown = false;

  /** @type {any[]} Filtered countries list. */
  public filteredCountriesList: any[] = [];

  /** @type {any[]} Filtered states list. */
  public filteredStatesList: any[] = [];

  /** @type {any[]} Filtered villages list. */
  public filteredVillagesList: any[] = [];

  /** @type {any[]} Limited villages list. */
  public limitedVillagesList: any[] = [];

  /** @type {string} Country search text. */
  public countrySearch: string = '';

  /** @type {string} State search text. */
  public stateSearch: string = '';

  /** @type {string} Village search text. */
  public villageSearch: string = '';

  /** @type {string} Action type (add/edit). */
  public actionType: string;

  /** @type {Array<PersonsModel>} List of business admin persons. */
  public businessAdminPersons: Array<PersonsModel>;

  /** @type {any} Feed provider person ID. */
  public feedProviderPersonId: any;

  /** @type {boolean} List loader flag. */
  public listLoader = false;              // Show listLoader spinner

  /** @type {number} Start index for batch loading. */
  public startIndex = 0;             // Tracks how much has been loaded

  /** @type {boolean} Flag for filtering state. */
  public isFiltering: boolean = false;

  /** @type {boolean} Flag for empty list. */
  public noList: boolean = false;

  /** @type {any} Scroll timeout. */
  private scrollTimeout: any;

  /** @type {any} Search timeout. */
  searchTimeout: any;

  /** @type {number} Form field count. */
  public formFieldCount: number = 0;

  /** @type {boolean} Flag to show form. */
  public showForm: boolean = false;

  /** @type {boolean} Flag for country change. */
  public isCountryChanged: boolean = false;

  /** @type {boolean} Flag for provider selection. */
  public isProviderSelected: boolean = false;

  /** @type {boolean} Flag for SME selection. */
  public isSmeSElected: boolean = false;

  /** @type {boolean} Flag for Buyer selection. */
  public isBuyerSelected: boolean = false;

  /** @type {Date} Max date for SME contract. */
  SMEMaxDate = new Date();

  /** @type {Date} Max date for Buyer contract. */
  BuyerMaxDate = new Date();

  /** @type {{startDate: moment.Moment, endDate: moment.Moment}} Selected SME date range. */
  SMESelected: {startDate: moment.Moment, endDate: moment.Moment};

  /** @type {{startDate: moment.Moment, endDate: moment.Moment}} Selected Buyer date range. */
  BuyerSelected: {startDate: moment.Moment, endDate: moment.Moment};


  /**
   * Constructor for LocationComponent.
   * Initializes services and models.
   *
   * @param {ToastrService} toasterService - Service for toast notifications.
   * @param {PersonsService} personService - Service for person operations.
   * @param {BusinessTypeService} businessTypeService - Service for business types.
   * @param {BusinessEntityService} businessEntityService - Service for business entities.
   * @param {FarmSiteService} farmSiteService - Service for farm sites.
   * @param {CustomerPondService} customerPondService - Service for customer ponds.
   * @param {WaterTypeService} waterTypeService - Service for water types.
   * @param {SoilTypeService} soilTypeService - Service for soil types.
   * @param {PersonsService} personsService - Service for persons (duplicate injection).
   * @param {ImageDetailsService} imageDetailService - Service for image details.
   * @param {Router} router - Angular Router.
   * @param {ActivatedRoute} route - Active route service.
   * @param {Sidenavaction} sidenavaction - Service for side navigation.
   * @param {FormBuilder} formBuilder - Service for building reactive forms.
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @param {PreviousRouteService} previousRouteService - Service for tracking route history.
   * @param {CountriesAPIService} countriesAPIService - Service for fetching countries.
   * @param {StatesAPIService} statesAPIService - Service for fetching states and villages.
   * @param {NgZone} ngZone - Angular Zone service.
   * @param {ChangeDetectorRef} cdr - Change detector reference.
   */
  constructor(private toasterService: ToastrService, public personService: PersonsService, public businessTypeService: BusinessTypeService, public businessEntityService: BusinessEntityService, public farmSiteService: FarmSiteService, public customerPondService: CustomerPondService, public waterTypeService: WaterTypeService, public soilTypeService: SoilTypeService, public personsService: PersonsService, public imageDetailService: ImageDetailsService,
              public router: Router, public route: ActivatedRoute, public sidenavaction: Sidenavaction, public formBuilder: FormBuilder, private locationStrategy: LocationStrategy,
              private previousRouteService: PreviousRouteService, public countriesAPIService: CountriesAPIService, public statesAPIService: StatesAPIService, private ngZone: NgZone, private cdr: ChangeDetectorRef) {
    this.farmSite = new FarmSiteModel();
    this.bioSecurity = new BioSecurity();
    this.smeContract = new SMEContract();
    this.buyerContract = new BuyerContract();
    this.contract = new Contract();
    this.address = new IAddress();
    this.waterType = new IWaterType();
    this.soilType = new ISoilType();
    this.villagesList = [];
  }

  /**
   * @lifecycle ngOnInit
   * @async
   * @description
   * Lifecycle hook that is called after data-bound properties of a directive are initialized.
   * - Sets previous route.
   * - Initializes form groups and validators.
   * - Fetches initial data (Countries, Business Types, etc.).
   * - Handles route parameters for edit/add modes.
   */
  public async ngOnInit() {
    window.scrollTo(0, 0);
    this.farmSites = [];
    this.farmSitesList = [];
    this.filteredFarmSites = [];
    this.isFiltering = false;

    this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
    this.appPermission = this.route.snapshot.data['rolepermission'];
    // // validation for farm site
    this.addFarmsiteForm = this.formBuilder.group({
      farmNameAtLocation: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(30), Validators.pattern('^[a-zA-Z\\s\\W]*$')]],
      yearStarted: ['', Validators.required],
      waterTypeId: ['', Validators.required],
      soilTypeId: ['', Validators.required],
      ownership: ['', Validators.required],
      countryName: ['', [Validators.required]],
      stateName: ['', [Validators.required]],
      villageName: ['', [Validators.required]],
      totalFarmArea: ['', Validators.pattern('^[0-9]*.[0-9]*$')],
      businessId: [null],
      providerBusinessId: [null],
      buyerbusinessId: [null],
      SMEDateRangeForm: this.formBuilder.group({
        start: [null],
        end: [null]
      }),
      BuyerDateRangeForm: this.formBuilder.group({
        start: [null],
        end: [null]
      }),
      buyerstartdate: [null],
      buyerenddate: [null],
      enablesme: [null],
      providerOrSme: [null],
      providertech: [null],
      enablebuyer: [null],
      enablechartuser: [null],
      persontype: [null],
      chatUserConfig: [null],
      providerChatUserConfig: [null],
      buyerUserConfig: [null],
      personId: [null],
      providerPersonId: [null],
      buyerPersonId: [null],
      numberOfGrowoutPonds: [null],
      waterspreadArea: [null],
      numberOfNurseries: [null],
      bioFencing: [null],
      bioHWandFA: [null],
      bioCrabFencing: [null],
      bioReservoirs: [null],
      bioBirdFencing: [null],
      bioFilters: [null],
      ETSOnPremises: [null],
      labOnPremises: [null]
    });

    // Calculate field count after form is built
    this.formFieldCount = Object.keys(this.addFarmsiteForm.controls).length;
    // // validation for farm site
    try {
      this.keyword = 'name';
      this.statekeyword = 'name';
      this.villagekeyword = 'name';
      this.locationCreationDoubleClick = false;
      this.stateVillageDisabled = false;
      this.submitButton = false;
      this.updateButton = false;
      /*login User data*/
      this.loginPersonType = localStorage.getItem('loginPersonType');
      this.loginUserBusinessCategory = localStorage.getItem('loginBusinessCategory');
      this.loginBusinessID = localStorage.businessID;
      /*login User data*/
      this.pondArr = [];
      this.countries = (await this.countriesAPIService.getAllCountriesList()).filter((country) => ['ES', 'ID', 'VN', 'NG', 'MY', 'AU', 'EC', 'SA', 'IN'].includes(country.countryCode));
      if (this.countries) {
        this.countrySearch = 'India';
        this.country = 'India';
        const indianCountryIndexPosition = this.countries.findIndex((country) => country.name == 'India');
        if (indianCountryIndexPosition != -1) {
          const objectToMove = this.countries.splice(indianCountryIndexPosition, 1);
          this.countries.unshift(objectToMove[0]);
          this.filteredCountriesList = this.countries;
          this.getStates();
        }
      }
      this.countrySearch = 'India';
      this.country = 'India';
      this.address = new IAddress();
      this.waterareavalidation = false;
      this.isSMEChecked = false;
      this.providerOrSme = null;
      this.isBuyerChecked = false;
      this.waterType.waterTypeId = undefined;
      this.soilType.soilTypeId = undefined;
      this.smeContract.SMEPersonID = '';
      this.smeContract.SMEBusinessID = '';
      this.smeContract.startDate = '';
      this.smeContract.endDate = '';
      this.buyerContract.buyerPersonID = '';
      this.buyerContract.buyerBusinessID = '';
      this.buyerContract.startDate = '';
      this.buyerContract.endDate = '';
      this.farmSite.chatUserConfig = '';
      this.farmSite.buyerUserConfig = '';
      this.contract.feedProviderID = '';
      this.contract.feedProviderPersonID = '';
      this.farmSite.providerUserConfig = [];
      this.bioFencing = false;
      this.bioHWandFA = false;
      this.bioCrabFencing = false;
      this.bioReservoirs = false;
      this.bioBirdFencing = false;
      this.bioFilters = false;
      // this.addContractDetails();
      this.villagesList = [];
      this.yearStartedArr = [];
      const curryear = new Date().getFullYear();
      for (let i = 1947; i < curryear; i++) {
        this.yearStartedArr.push(i + 1);
      }
      this.initializeSMEDatePicker();
      this.initializeBuyerDatePicker();
      this.inIt();
      this.strArray = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      /********** Update farmsite **********/
      this.getStates();
      this.selectedYear = '';
      // Business Details registraion validation
      this.sidenavaction.x_navigation();
      this.address.village = '';
      this.businessTypes = await this.businessTypeService.getAllBusinessTypes();
      this.getAllFarmProvider();
      this.getSmeBusiness();
      this.getBuyerBusiness();
      this.getProviderBusiness();
    } catch (error) {
      this.toasterService.error(error?.error?.message || error?.message || error);
    }
  }

  /**
    * @method initializeSMEDatePicker
    * @description Initializes the date picker for SME contract start and end dates.
    * Subscribes to value changes.
    */
  public initializeSMEDatePicker(): void {
    this.addFarmsiteForm.get('SMEDateRangeForm').valueChanges.subscribe(val => {
      const { start, end } = val;
      if (!start || !end) {
      }
    });
  }

  /**
   * @method smeOnPickerClosed
   * @description Handles the closing of the SME date picker.
   */
  public smeOnPickerClosed(): void {
    const { start, end } = this.addFarmsiteForm.get('SMEDateRangeForm').value;

    if (start && !end) {
      // Only start selected → default end = start
      this.addFarmsiteForm.get('SMEDateRangeForm').patchValue({ end: start }, { emitEvent: false });
      this.smeOnDateChanged({ startDate: start, endDate: start });
    } else if (start && end) {
      this.smeOnDateChanged({ startDate: start, endDate: end });
    }
  }

  /**
   * @method smeOnDateChanged
   * @description Updates the selected SME date range.
   * @param {{ startDate: Date; endDate: Date }} range - The selected date range.
   */
  public smeOnDateChanged(range: { startDate: Date; endDate: Date }): void {
    this.SMESelected = {
      startDate: moment(range.startDate),
      endDate: moment(range.endDate)
    };
  }

  /**
   * @method clearSMEDateRange
   * @description Clears the SME date range selection.
   */
  public clearSMEDateRange(): void {
    this.addFarmsiteForm.get('SMEDateRangeForm').reset();
    this.SMESelected = undefined;
  }

  /**
    * @method initializeBuyerDatePicker
    * @description Initializes the date picker for Buyer contract start and end dates.
    */
  public initializeBuyerDatePicker(): void {
    this.addFarmsiteForm.get('BuyerDateRangeForm').valueChanges.subscribe(val => {
      const { start, end } = val;
      if (!start || !end) {
      }
    });
  }

  /**
   * @method buyerOnPickerClosed
   * @description Handles the closing of the Buyer date picker.
   */
  public buyerOnPickerClosed(): void {
    const { start, end } = this.addFarmsiteForm.get('BuyerDateRangeForm').value;

    if (start && !end) {
      // Only start selected → default end = start
      this.addFarmsiteForm.get('BuyerDateRangeForm').patchValue({ end: start }, { emitEvent: false });
      this.buyerOnDateChanged({ startDate: start, endDate: start });
    } else if (start && end) {
      this.buyerOnDateChanged({ startDate: start, endDate: end });
    }
  }

  /**
   * @method buyerOnDateChanged
   * @description Updates the selected Buyer date range.
   * @param {{ startDate: Date; endDate: Date }} range - The selected date range.
   */
  public buyerOnDateChanged(range: { startDate: Date; endDate: Date }): void {
    this.BuyerSelected = {
      startDate: moment(range.startDate),
      endDate: moment(range.endDate)
    };
  }

  /**
   * @method clearBuyerDateRange
   * @description Clears the Buyer date range selection.
   */
  public clearBuyerDateRange(): void {
    this.addFarmsiteForm.get('BuyerDateRangeForm').reset();
    this.BuyerSelected = undefined;
  }

  /**
   * @lifecycle ngOnDestroy
   * @description
   * Lifecycle hook that is called when the component is destroyed.
   * Cancels all ongoing HTTP requests to prevent memory leaks.
   * @side-effect Cancels HTTP requests for all injected services.
   */
  public ngOnDestroy(): void {
    this.businessTypeService.cancelOnGoingHttpRequests();
    this.businessEntityService.cancelOnGoingHttpRequests();
    this.farmSiteService.cancelOnGoingHttpRequests();
    this.waterTypeService.cancelOnGoingHttpRequests();
    this.soilTypeService.cancelOnGoingHttpRequests();
    this.personsService.cancelOnGoingHttpRequests();
    this.customerPondService.cancelOnGoingHttpRequests();
    this.imageDetailService.cancelOnGoingHttpRequests();
  }

  /**
   * @method f
   * @description Getter for easy access to form controls in the template.
   * @returns {AbstractControl} Form controls.
   */
  public get f() {
    return this.addFarmsiteForm.controls;
  }

  private batchLoader: BatchLoader<any>;

  /**
   * @method inIt
   * @async
   * @description Initializes data loading for the component, including business entity details and farm sites.
   */
  public async inIt() {
    try {
      this.locationCreationDoubleClick = false;
      this.businessEntity = await this.businessEntityService.getOneByID(this.loginBusinessID);
      if (this.businessEntity) {
        if (this.businessEntity && this.businessEntity.branding_attachments && this.businessEntity.branding_attachments.length > 0 && this.businessEntity.branding_attachments[0] && this.businessEntity.branding_attachments[0].file_path) {
          let filePath = this.businessEntity.branding_attachments[0].file_path;
          const file = await this.businessEntityService.downloadFileFromS3Bucket(filePath);
          file.subscribe(fileBlob => {
            const readFile = new FileReader();
            readFile.readAsDataURL(fileBlob);
            readFile.onload = () => {
              this.loginUserBusinessIcon = readFile.result;
              if (this.loginUserBusinessIcon) {
                localStorage.setItem('loginBusinessIcon', filePath);
              }
            };
          });
        }
      }
      this.loginUserBusinessIcon = localStorage.getItem('loginBusinessIcon'); // login person Business name
      this.farmSites = await this.farmSiteService.getFarmeSitesByFarmId(this.businessEntity.id);
      this.pgLoad = true;
      this.initializeBatchLoader();
      this.batchLoader?.reset();
      await this.batchLoader?.loadNextBatch(true);
      this.stateVillageDisabled = false;
      this.countryCode = this.businessEntity.address[0].countryCode;
      this.waterTypes = await this.waterTypeService.getAllWaterTypes();
      this.soilTypes = await this.soilTypeService.getAllSoilTypes();
      this.personsByBusinessId = await this.personsService.getPersonsByBusinessID(this.loginBusinessID);
      this.personsByProviderBusinessId = await this.personsService.getPersonsbyBusinessID(this.loginBusinessID);
    } catch (error) {
      this.toasterService.error(error?.error?.message || error?.message || error);
    }
  }

  /**
   * @method onScroll
   * @description Host listener for window scroll events. Triggers batch loading.
   * @lifecycle HostListener
   */
  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  /**
   * @method checkScrollAndLoad
   * @private
   * @description Checks if scroll position is near bottom and loads next batch.
   */
  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 && !this.batchLoader?.isLoading()) {
        this.batchLoader?.loadNextBatch();
      }
    }, 50);
  }

  /**
   * @method initializeBatchLoader
   * @private
   * @description Initializes the `BatchLoader` utility.
   */
  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.isFiltering ? this.filteredFarmSites : this.farmSites,
      (updatedList) => {
        this.farmSitesList = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined,
      (loading) => this.listLoader = loading
    );
  }

  /**
   * @method getSMEPersonsByBusinessID
   * @async
   * @description Fetches SME persons associated with a business ID.
   * @param {string} businessID - The business ID.
   */
  public async getSMEPersonsByBusinessID(businessID: string) {
    if (businessID) {
      try {
        this.persons = await this.personsService.getSMEPersonsByBusinessID(businessID);
      } catch (error) {
        this.toasterService.error(error?.error?.message || error?.message || error);
      }
    }
  }

  /**
   * @method getProviderPersonsByBusinessID
   * @async
   * @description Fetches provider persons associated with a business ID.
   * @param {string} businessID - The business ID.
   */
  public async getProviderPersonsByBusinessID(businessID: string) {
    if (businessID) {
      try {
        this.providerPersons = await this.personsService.getProviderPersonsByBusinessID(businessID);
      } catch (error) {
        this.toasterService.error(error?.error?.message || error?.message || error);
      }
    }
  }

  /**
   * @method getBuyerPersonsByBusinessID
   * @async
   * @description Fetches buyer persons associated with a business ID.
   * @param {string} businessID - The business ID.
   */
  public async getBuyerPersonsByBusinessID(businessID: string) {
    if (businessID) {
      try {
        this.buyerPersons = await this.personsService.getBuyerPersonsByBusinessID(businessID);
      } catch (error) {
        this.toasterService.error(error?.error?.message || error?.message || error);
      }
    }
  }

  /**
   * @method checkPincode
   * @async
   * @description Fetches latitude and longitude for the given location details.
   * @param {string} village - Village name.
   * @param {string} state - State name.
   * @param {string} mandal - Mandal name.
   * @param {string} district - District name.
   */
  public async checkPincode(village: string, state: string, mandal: string, district: string) {
    try {
      this.latLongs = await this.farmSiteService.latLongsData(village, state, mandal, district);
      if (this.latLongs.results.length > 0) {
        this.address.latitude = this.latLongs.results[0].geometry.location.lat;
        this.address.longitude = this.latLongs.results[0].geometry.location.lng;
      }
    } catch (error) {
      this.toasterService.error(error?.error?.message || error?.message || error);
    }
  }

  /**
   * @method providerOrSmeChange
   * @description Handles the toggle between Provider and SME selection.
   */
  public providerOrSmeChange() {
    this.clearSMEDateRange();
    if (this.providerOrSme == true) {
      this.isProviderSelected = true;
      this.isSmeSElected = false;
      this.smeContract.SMEBusinessID = '';
      this.smeContract.SMEPersonID = '';
      this.smeContract.startDate = '';
      this.smeContract.endDate = '';
      this.farmSite.SMEContract = [];
      this.farmSite.chatUserConfig = '';
    } else {
      this.isSmeSElected = true;
      this.isProviderSelected = false;
      this.contract.feedProviderID = '';
      this.contract.feedProviderPersonID = '';
      this.farmSite.contract = [];
      this.farmSite.providerUserConfig = [];
    }
  }

  /**
   * @method addFarmSite
   * @async
   * @description Submits the form to create a new farm site.
   * Validates input, constructs payload, and calls service.
   */
  public async addFarmSite() {

    this.submitted = true;
    this.addFarmsiteForm.markAllAsTouched();

    if (this.addFarmsiteForm.invalid) return false;

    if (this.waterType.waterTypeId && _.filter(this.waterTypes, ['id', Number(this.waterType.waterTypeId)])) {
      const watertypedata = _.filter(this.waterTypes, ['id', Number(this.waterType.waterTypeId)]);
      this.waterType.name = watertypedata[0].name;
    }
    // changed same for address and waterTypes
    if (this.soilType.soilTypeId && _.filter(this.soilTypes, ['id', Number(this.soilType.soilTypeId)])) {
      const soiltypedata = _.filter(this.soilTypes, ['id', Number(this.soilType.soilTypeId)]);
      this.soilType.name = soiltypedata[0].name;
    }
    this.farmSite.waterType = this.waterType;
    this.farmSite.soilType = this.soilType;
    this.farmSite.bioSecurity = [];
    if (this.bioFencing) {
      this.farmSite.bioSecurity.push({name: 'Fencing'});
    }
    if (this.bioHWandFA) {
      this.farmSite.bioSecurity.push({name: 'Hand Wash and Footbath Area'});
    }
    if (this.bioCrabFencing) {
      this.farmSite.bioSecurity.push({name: 'Crab Fencing'});
    }
    if (this.bioReservoirs) {
      this.farmSite.bioSecurity.push({name: 'Reservoirs'});
    }
    if (this.bioBirdFencing) {
      this.farmSite.bioSecurity.push({name: 'Bird Fencing'});
    }
    if (this.bioFilters) {
      this.farmSite.bioSecurity.push({name: 'Filters'});
    }
    // Checck with this
    this.farmSite.businessID = this.farmbusinessid;
    if (this.loginPersonType == 'Business Admin' && this.loginUserBusinessCategory == 'Provider') {
      if (this.feedProviderPersonId) {
        this.farmSite.contract = [
          {
            feedProviderID: "true",
            feedProviderPersonID: this.feedProviderPersonId,
            startDate: null,
            endDate: null
          }
        ];
      } else {
        this.farmSite.contract = [
          {
            feedProviderID: "true",
            feedProviderPersonID: "true",
            startDate: null,
            endDate: null
          }
        ];
      }
    } else {
      // Provider Contract
      if (this.isSMEChecked && this.providerOrSme) {
        if (!this.contract.feedProviderID) {
          this.toasterService.error('Please select provider business');
          return;
        } else if (!this.contract.feedProviderPersonID) {
          this.toasterService.error('Please select provider');
          return;
        } else if (!this.farmSite.providerUserConfig) {
          this.toasterService.error('Please select chatty user');
          return;
        }
        this.farmSite.contract = [
          {
            feedProviderID: this.contract.feedProviderID,
            feedProviderPersonID: this.contract.feedProviderPersonID,
          }
        ];
        if (this.farmSite.providerUserConfig) {
          let providerConfig = this.farmSite.providerUserConfig;
          this.farmSite.providerUserConfig = [providerConfig];
        }
      }
    }
    // SME Contract
    if (this.isSMEChecked && this.providerOrSme == false) {
      if (!this.smeContract.SMEBusinessID) {
        this.toasterService.error('Please select sme business');
        return;
      } else if (!this.smeContract.SMEPersonID) {
        this.toasterService.error('Please select sme person');
        return;
      } else if (!this.SMESelected) {
        this.toasterService.error('Please select sme contract date range');
        return;
      }
    }
    // Checck with this
    if (this.isSMEChecked && this.providerOrSme == false) {
      this.farmSite.SMEContract = [
        {
          SMEBusinessID: this.smeContract.SMEBusinessID,
          SMEPersonID: this.smeContract.SMEPersonID,
          startDate: moment(this.SMESelected.startDate).format('YYYY-MM-DD'),
          endDate: moment(this.SMESelected.endDate).format('YYYY-MM-DD')
        }
      ];
    } else {
      delete this.farmSite.SMEContract;
    }
    // Buyer Contract
    if (this.isBuyerChecked) {
      if (!this.buyerContract.buyerBusinessID) {
        this.toasterService.error('Please select buyer business');
        return;
      } else if (!this.buyerContract.buyerPersonID) {
        this.toasterService.error('Please select buyer person');
        return;
      } else if (!this.BuyerSelected) {
        this.toasterService.error('Please select buyer contract date range');
        return;
      }
    }
    // Checck with this
    if (this.isBuyerChecked) {
      this.farmSite.buyerContract = [
        {
          buyerBusinessID: this.buyerContract.buyerBusinessID,
          buyerPersonID: this.buyerContract.buyerPersonID,
          startDate: moment(this.BuyerSelected.startDate).format('YYYY-MM-DD'),
          endDate: moment(this.BuyerSelected.endDate).format('YYYY-MM-DD')
        }
      ];
    } else {
      delete this.farmSite.buyerContract;
    }
    /*address*/
    this.farmSite.address = {
      village: this.address.village.toLowerCase().includes(',') ? this.address.village.split(',')[0] : this.address.village,
      taluk: this.address.village.toLowerCase().includes(',') && (this.address.village.match(/,/g)).length == 2 ? this.address.village.split(',')[1] : (this.address.village.toLowerCase().includes(',') && (this.address.village.match(/,/g)).length == 1 ? this.address.village.split(',')[0] : this.address.state),
      state: this.address.state,
      pincode: this.address.pincode,
      mandal: this.address.village.toLowerCase().includes(',') && (this.address.village.match(/,/g)).length == 2 ? this.address.village.split(',')[1] : (this.address.village.toLowerCase().includes(',') && (this.address.village.match(/,/g)).length == 1 ? this.address.village.split(',')[0] : this.address.state),
      district: this.address.village.toLowerCase().includes(',') && (this.address.village.match(/,/g)).length == 2 ? this.address.village.split(',')[2] : (this.address.village.toLowerCase().includes(',') && (this.address.village.match(/,/g)).length == 1 ? this.address.village.split(',')[1] : this.address.state),
      latitude: this.address.latitude,
      longitude: this.address.longitude,
      country: this.countrySearch
    };
    this.farmSite.personType = this.farmSite.chatUserConfig;
    try {
      this.locationCreationDoubleClick = true;
      this.farmSite = await this.farmSiteService.create(this.farmSite);
      this.toasterService.success('Farmsite created successfully', '', {
        timeOut: 2000
      });
     this.locationCreationDoubleClick = false;
     this.showForm = false;
      if (this.loginPersonType == 'Business Admin' && this.loginUserBusinessCategory == 'Provider') {
        // this.router.navigate(['/BusinessFP-Farm-Farmsite', this.loginBusinessID, this.navparamBusinessID]);
        this.router.navigate(['/home/BusinessFP-Farm-Farmsite', this.loginBusinessID, this.navparamBusinessID, this.paramsBusiness]);
      }
      this.farmSite = new FarmSiteModel();
      this.villageChanged = false;
      this.stateVillageDisabled = false;
      this.isSMEChecked = false;
      this.isBuyerChecked = false;
      this.farmSite.contract = [];
      this.farmSite.SMEContract = [];
      this.smeContract.SMEPersonID = '';
      this.smeContract.SMEBusinessID = '';
      this.smeContract.startDate = '';
      this.smeContract.endDate = '';
      this.farmSite.buyerContract = [];
      this.buyerContract.buyerPersonID = '';
      this.buyerContract.buyerBusinessID = '';
      this.buyerContract.startDate = '';
      this.buyerContract.endDate = '';
      this.contract.feedProviderID = '';
      this.contract.feedProviderPersonID = '';
      this.farmSite.providerUserConfig = [];
      this.persons = undefined;
      this.farmSite.chatUserConfig = undefined;
      this.farmSite.buyerUserConfig = undefined;
      this.address.village = '';
      this.address.taluk = '';
      this.address.state = '';
      this.address.pincode = undefined;
      this.address.mandal = '';
      this.address.district = '';
      this.waterType.waterTypeId = undefined;
      this.soilType.soilTypeId = undefined;
      this.bioFencing = undefined;
      this.bioHWandFA = undefined;
      this.bioCrabFencing = undefined;
      this.bioReservoirs = undefined;
      this.bioBirdFencing = undefined;
      this.bioFilters = undefined;
      this.villageItem = undefined;
      this.stateitem = undefined;
      this.item = undefined;
      this.country = undefined;
      this.farmSite.ownership = undefined;
      this.farmSite.yearStarted = undefined;
      this.filteredVillagesList = new Array();
      this.filteredStatesList = new Array();
      this.address = new IAddress();
      this.closeForm();
      await this.inIt();
    } catch (error) {
      this.toasterService.error(error?.error?.message || error?.message || error);
      this.locationCreationDoubleClick = false;
      this.submitButton = true;
    }
  }

  /**
   * @method changewaterspreadare
   * @description Validates that water spread area is not greater than total farm area.
   * Sets `waterareavalidation` flag.
   */
  public changewaterspreadare() {
    if (Number(this.farmSite.waterSpreadArea) >= Number(this.farmSite.totalFarmArea)) {
      this.waterareavalidation = true;
    } else {
      this.waterareavalidation = false;
    }
  }

  /**
   * @method numberValidation
   * @description Validates input to allow only numbers and decimals.
   * @param {any} event - The keyboard event.
   */
  public numberValidation(event: any) {
    const key = event.key;
    const value = event.target.value;
    // Allow numbers, the dot (decimal point), and the Backspace key
    if (
      !/^\d$/.test(key) && // Check if the entered character is a number
      key !== '.' && // Check if the entered character is a dot (decimal point)
      key !== 'Backspace' // Check if the Backspace key was pressed
    ) {
      event.preventDefault();
    }

    // Allow only one dot (decimal point)
    if (key === '.' && value.includes('.')) {
      event.preventDefault();
    }

    // Disallow the first letter being a dot
    if (key === '.' && value === '') {
      event.preventDefault();
    }
  }

  /**
   * @method updateFarmSite
   * @async
   * @description Submits the form to update an existing farm site.
   * Validates input, constructs payload, and calls service.
   */
  public async updateFarmSite() {
    this.submitted = true;
    this.addFarmsiteForm.markAllAsTouched();
    // stop here if form is invalid
    if (this.addFarmsiteForm.invalid) {
      return;
    }
    // stop here if form is invalid
    try {
      // this.pinCodes = await this.businessEntityService.checkPincode(this.address.pincode, this.countryCode);
      // if (this.pinCodes && this.pinCodes.result.length > 0) {
      this.farmSite.bioSecurity = [];
      if (this.bioFencing) {
        this.farmSite.bioSecurity.push({'name': 'Fencing'});
      }
      if (this.bioHWandFA) {
        this.farmSite.bioSecurity.push({'name': 'Hand Wash and Footbath Area'});
      }
      if (this.bioCrabFencing) {
        this.farmSite.bioSecurity.push({'name': 'Crab Fencing'});
      }
      if (this.bioReservoirs) {
        this.farmSite.bioSecurity.push({'name': 'Reservoirs'});
      }
      if (this.bioBirdFencing) {
        this.farmSite.bioSecurity.push({'name': 'Bird Fencing'});
      }
      if (this.bioFilters) {
        this.farmSite.bioSecurity.push({'name': 'Filters'});
      }
      this.waterTypes = await this.waterTypeService.getAllWaterTypes();
      if (this.waterType.waterTypeId && _.filter(this.waterTypes, ['id', Number(this.waterType.waterTypeId)])) {
        const watertypedata = _.filter(this.waterTypes, ['id', Number(this.waterType.waterTypeId)]);
        this.waterType.name = watertypedata[0].name;
      }
      this.soilTypes = await this.soilTypeService.getAllSoilTypes();
      if (this.soilType.soilTypeId && _.filter(this.soilTypes, ['id', Number(this.soilType.soilTypeId)])) {
        const soiltypedata = _.filter(this.soilTypes, ['id', Number(this.soilType.soilTypeId)]);
        this.soilType.name = soiltypedata[0].name;
      }
      this.farmSite.waterType = this.waterType;
      this.farmSite.soilType = this.soilType;
      if (this.loginPersonType == 'Business Admin' && this.loginUserBusinessCategory == 'Provider') {
        if (this.feedProviderPersonId != 'undefined') {
          this.farmSite.contract = [
            {
              feedProviderID: "true",
              feedProviderPersonID: this.feedProviderPersonId,
              startDate: null,
              endDate: null
            }
          ];
        } else {
          this.farmSite.contract = [
            {
              feedProviderID: "true",
              feedProviderPersonID: "true",
              startDate: null,
              endDate: null
            }
          ];
        }
      } else {
        // provider contract
        if (this.providerOrSme) {
          this.farmSite.contract = [
            {
              feedProviderID: this.contract.feedProviderID,
              feedProviderPersonID: this.contract.feedProviderPersonID,
              isCompleted: false,
              startDate: null,
              endDate: null
            }
          ];
        } else if ((this.farmSite && this.farmSite.contract && this.farmSite.contract.length > 0) && (this.farmSite.contract[0].feedProviderID && this.farmSite.contract[0].feedProviderPersonID)) {
          this.farmSite.contract = [{
            startDate: null,
            endDate: null,
            isCompleted: false,
            feedProviderID: this.contract.feedProviderID ? this.contract.feedProviderID : this.farmSite.contract[0].feedProviderID,
            feedProviderPersonID: this.contract.feedProviderPersonID ? this.contract.feedProviderPersonID : this.farmSite.contract[0].feedProviderPersonID,
          }];
          this.farmSite.providerUserConfig = [];
        } else {
          this.farmSite.providerUserConfig = [];
        }
      }
      // SME Contract
      if (this.isSMEChecked && this.providerOrSme == false) {
        if (!this.smeContract.SMEBusinessID) {
          this.toasterService.error('Please select sme business');
          return;
        } else if (!this.smeContract.SMEPersonID) {
          this.toasterService.error('Please select sme person');
          return;
        } else if (!this.SMESelected) {
          this.toasterService.error('Please select sme date range');
          return;
        }
      }
      if (this.isSMEChecked && this.providerOrSme == false) {
        this.farmSite.SMEContract = [
          {
            SMEBusinessID: this.smeContract.SMEBusinessID,
            SMEPersonID: this.smeContract.SMEPersonID,
            startDate: moment(this.SMESelected.startDate).format('YYYY-MM-DD'),
            endDate: moment(this.SMESelected.endDate).format('YYYY-MM-DD'),
          }
        ];
        let chatUser = this.farmSite.chatUserConfig;
        this.farmSite.chatUserConfig = chatUser;
        // this.farmSite.chatUserConfig = this.farmSite.chatUserConfig;
      } else if ((this.farmSite && this.farmSite.SMEContract && this.farmSite.SMEContract.length > 0) && (this.farmSite.SMEContract[0].SMEBusinessID && this.farmSite.SMEContract[0].SMEPersonID && this.farmSite.SMEContract[0].startDate && this.farmSite.SMEContract[0].endDate)) {
        this.farmSite.SMEContract = [{
          SMEBusinessID: this.smeContract.SMEBusinessID ? this.smeContract.SMEBusinessID : this.farmSite.SMEContract[0].SMEBusinessID,
          SMEPersonID: this.smeContract.SMEPersonID ? this.smeContract.SMEPersonID : this.farmSite.SMEContract[0].SMEPersonID,
          startDate: this.SMESelected.startDate ? moment(this.SMESelected.startDate).format('YYYY-MM-DD') : this.farmSite.SMEContract[0].startDate,
          endDate: this.SMESelected.endDate ? moment(this.SMESelected.endDate).format('YYYY-MM-DD') : this.farmSite.SMEContract[0].endDate,
          isCompleted: true
        }];
        this.farmSite.chatUserConfig = '';
      } else {
        this.farmSite.SMEContract = [{
          startDate: '',
          endDate: '',
          isCompleted: true
        }];
        this.farmSite.chatUserConfig = '';
      }
      /*if provider or sme unselect below code execute*/
      if (this.isSMEChecked == false && this.providerOrSme == true && this.farmSite.contract.length > 0) {
        this.farmSite.contract[0].isCompleted = true;
      }
      if (this.isSMEChecked == false && this.providerOrSme == false && this.farmSite.SMEContract.length > 0) {
        this.farmSite.SMEContract[0].isCompleted = true;
      }
      // Buyer Contract
      if (this.isBuyerChecked) {
        if (!this.buyerContract.buyerBusinessID) {
          this.toasterService.error('Please select buyer business');
          return;
        } else if (!this.buyerContract.buyerPersonID) {
          this.toasterService.error('Please select buyer person');
          return;
        } else if (!this.BuyerSelected) {
          this.toasterService.error('Please select buyer date range');
          return;
        }
      }
      if (this.isBuyerChecked) {
        this.farmSite.buyerContract = [
          {
            buyerBusinessID: this.buyerContract.buyerBusinessID,
            buyerPersonID: this.buyerContract.buyerPersonID,
            startDate: moment(this.BuyerSelected.startDate).format('YYYY-MM-DD'),
            endDate: moment(this.BuyerSelected.endDate).format('YYYY-MM-DD'),
          }
        ];
        this.farmSite.buyerUserConfig = this.farmSite.buyerUserConfig;
      } else if ((this.farmSite && this.farmSite.buyerContract && this.farmSite.buyerContract.length > 0) && (this.farmSite.buyerContract[0].buyerBusinessID && this.farmSite.buyerContract[0].buyerPersonID && this.farmSite.buyerContract[0].startDate && this.farmSite.buyerContract[0].endDate)) {
        this.farmSite.buyerContract = [{
          buyerBusinessID: this.buyerContract.buyerBusinessID ? this.buyerContract.buyerBusinessID : this.farmSite.buyerContract[0].buyerBusinessID,
          buyerPersonID: this.buyerContract.buyerPersonID ? this.buyerContract.buyerPersonID : this.farmSite.buyerContract[0].buyerPersonID,
          startDate: this.BuyerSelected.startDate ? moment(this.BuyerSelected.startDate).format('YYYY-MM-DD') : this.farmSite.buyerContract[0].startDate,
          endDate: this.BuyerSelected.endDate ? moment(this.BuyerSelected.endDate).format('YYYY-MM-DD') : this.farmSite.buyerContract[0].endDate,
          isCompleted: true
        }];
        this.farmSite.buyerUserConfig = '';
        // this.farmSite.chatUserConfig = ''
      } else {
        this.farmSite.buyerContract = [{
          startDate: '',
          endDate: '',
          isCompleted: true
        }];
        this.farmSite.buyerUserConfig = '';
      }
      // address
      if (this.villageChanged == true) {
        this.farmSite.address = {
          village: this.address.village.toLowerCase().includes(',') ? this.address.village.split(',')[0] : this.address.village,
          taluk: this.address.village.toLowerCase().includes(',') && (this.address.village.match(/,/g)).length == 2 ? this.address.village.split(',')[1] : (this.address.village.toLowerCase().includes(',') && (this.address.village.match(/,/g)).length == 1 ? this.address.village.split(',')[0] : this.address.state),
          state: this.address.state,
          pincode: this.address.pincode,
          mandal: this.address.village.toLowerCase().includes(',') && (this.address.village.match(/,/g)).length == 2 ? this.address.village.split(',')[1] : (this.address.village.toLowerCase().includes(',') && (this.address.village.match(/,/g)).length == 1 ? this.address.village.split(',')[0] : this.address.state),
          district: this.address.village.toLowerCase().includes(',') && (this.address.village.match(/,/g)).length == 2 ? this.address.village.split(',')[2] : (this.address.village.toLowerCase().includes(',') && (this.address.village.match(/,/g)).length == 1 ? this.address.village.split(',')[1] : this.address.state),
          latitude: this.address.latitude,
          longitude: this.address.longitude,
          country: this.countrySearch
        };
      }
      this.farmSite.businessID = this.farmbusinessid;
      this.farmSite.personType = this.farmSite.chatUserConfig;
      this.locationCreationDoubleClick = true;
      const originalProviderUserConfig = this.farmSite.providerUserConfig;
      if (typeof originalProviderUserConfig === 'string') {
        this.farmSite.providerUserConfig = [originalProviderUserConfig]; // Convert the string to an array
      } else if (Array.isArray(originalProviderUserConfig)) {
        this.farmSite.providerUserConfig = originalProviderUserConfig; // Use the original array
      } else {
        this.farmSite.providerUserConfig = []; // Default case: assign an empty array
      }
      const updateFarmSite = Object.assign({}, this.farmSite);
      delete updateFarmSite.id;
      this.farmSite = await this.farmSiteService.updateOneByID(this.farmSite.id, updateFarmSite);
      this.toasterService.success('Farmsite updated successfully', '', {
        timeOut: 2000
      });
      if (this.loginPersonType == 'Business Admin' && this.loginUserBusinessCategory == 'Provider') {
        this.cancel();
      }
      this.stateVillageDisabled = false;
      this.farmSite = new FarmSiteModel();
      this.address = new Address();
      this.stateSearch = '';
      this.villageSearch = '';
      this.isSMEChecked = false;
      this.providerOrSme = false;
      this.contract.feedProviderID = '';
      this.contract.feedProviderPersonID = '';
      this.farmSite.providerUserConfig = [];
      this.villageChanged = false;
      this.submitted = false;
      this.farmSite.contract = [];
      this.address.pincode = undefined;
      this.address.village = '';
      this.waterType.waterTypeId = undefined;
      this.soilType.soilTypeId = undefined;
      this.bioFencing = undefined;
      this.bioHWandFA = undefined;
      this.bioCrabFencing = undefined;
      this.bioReservoirs = undefined;
      this.bioBirdFencing = undefined;
      this.bioFilters = undefined;
      this.isSMEChecked = false;
      this.isBuyerChecked = false;
      this.farmSite.SMEContract = [];
      this.smeContract.SMEBusinessID = undefined;
      this.smeContract.SMEPersonID = undefined;
      this.smeContract.startDate = '';
      this.smeContract.endDate = '';
      this.farmSite.buyerContract = [];
      this.buyerContract.buyerPersonID = undefined;
      this.buyerContract.buyerBusinessID = undefined;
      this.buyerContract.startDate = '';
      this.buyerContract.endDate = '';
      this.villageItem = undefined;
      this.stateitem = undefined;
      this.item = undefined;
      this.country = undefined;
      this.farmSite.ownership = undefined;
      this.farmSite.yearStarted = undefined;
      this.filteredVillagesList = new Array();
      this.filteredStatesList = new Array();
      this.address = new IAddress();
      this.closeForm();
      this.inIt();
    } catch (error) {
      this.toasterService.error(error?.error?.message || error?.message || error);
      this.locationCreationDoubleClick = false;
      this.updateButton = true;
    }
  }

  /**
   * @method farmSiteDetailsByID
   * @async
   * @description Fetches farm site details by ID for editing.
   * @param {any} farmSiteID - The farm site ID.
   */
  public async farmSiteDetailsByID(farmSiteID: any) {
    this.showForm = true;
    this.updateButton = true;
    this.submitButton = false;

    this.isSMEChecked = false;
    this.isBuyerChecked = false;
    this.isProviderSelected = false;
    this.isSmeSElected = false;

    this.providerOrSme = false;
    this.locationCreationDoubleClick = false;

    try {
      const foundFarmsite = await this.farmSiteService.getOneByID(farmSiteID)
      this.farmSite = foundFarmsite ? { ...foundFarmsite } : new FarmSiteModel(); // clone object
      this.address = this.farmSite.address;
      this.farmSite.yearStarted = Number(this.farmSite.yearStarted)
      if (this.address) {
        this.countrySearch = this.address.country || 'India';
        this.country = this.address.country || 'India';
        this.addFarmsiteForm.get('countryName')!.setValue(this.address.country);
        this.getStates();
        this.addFarmsiteForm.get('stateName')!.setValue(this.address.state);
        const stateObj = this.states.find(obj => obj.name == this.address.state);
        this.getVillages(stateObj);
        this.villageSearch = this.address.state == this.address.district ? this.address.village : (this.address.village == this.address.taluk ? this.address.village + ' , ' + this.address.district : this.address.village + ' , ' + this.address.mandal + ' , ' + this.address.district);
        this.addFarmsiteForm.get('villageName')!.setValue(this.villageSearch);
      }
      this.villageChanged = false;
      if (this.farmSite.ownership == '') {
        this.farmSite.ownership = undefined;
      }
      if (this.farmSite.waterType) {
        this.waterType = this.farmSite.waterType;
        this.waterType.waterTypeId = this.farmSite.waterType.waterTypeId;
      } else {
        this.farmSite.waterType = undefined;
      }
      if (this.farmSite.soilType) {
        this.soilType = this.farmSite.soilType;
        this.soilType.soilTypeId = this.farmSite.soilType.soilTypeId;
      } else {
        this.farmSite.waterType = undefined;
      }

      if ((this.farmSite && this.farmSite.contract && this.farmSite.contract.length > 0) && (this.farmSite.contract[0].feedProviderID)) {
        this.isSMEChecked = true;
        this.providerOrSme = true;
        this.isProviderSelected = true;
        this.farmSite.providerUserConfig = this.farmSite.providerUserConfig?.[0] ?? undefined;
      }
      if ((this.farmSite && this.farmSite.SMEContract && this.farmSite.SMEContract.length > 0) && (this.farmSite.SMEContract[0].SMEBusinessID && this.farmSite.SMEContract[0].SMEPersonID && this.farmSite.SMEContract[0].startDate && this.farmSite.SMEContract[0].endDate && this.farmSite.SMEContract[0].isCompleted == false)) {
        this.isSMEChecked = true;
        this.providerOrSme = false;
        this.isSmeSElected = true;
      }
      if ((this.farmSite && this.farmSite.buyerContract && this.farmSite.buyerContract.length > 0) && (this.farmSite.buyerContract[0].buyerBusinessID && this.farmSite.buyerContract[0].buyerPersonID && this.farmSite.buyerContract[0].startDate && this.farmSite.buyerContract[0].endDate && this.farmSite.buyerContract[0].isCompleted == false)) {
        this.isBuyerChecked = true;
      }
      if ((this.loginPersonType == 'Business Admin' && this.loginUserBusinessCategory == 'Provider')) {
        if (this.farmSite && this.farmSite.contract.length > 0) {
          this.feedProviderPersonId = this.farmSite.contract[0].feedProviderPersonID;
        }
      } else {
        // Provider
        if (this.isSMEChecked && this.providerOrSme) {
          if (this.farmSite && this.farmSite.contract.length > 0) {
            this.contract = this.farmSite.contract[0];
            if (this.contract.feedProviderID) {
              await this.getProviderPersonsByBusinessID(this.contract.feedProviderID);
            }
          }
          if (this.farmSite.providerUserConfig.length > 0) {
            this.providerChatUser = this.farmSite.providerUserConfig;
          } else {
            this.providerChatUser = [];
          }
        }
      }
      // SMEContract
      if (this.isSMEChecked && this.providerOrSme == false) {
        if (this.farmSite && this.farmSite.SMEContract.length > 0) {
          this.smeContract = this.farmSite.SMEContract[0];
          if (this.smeContract.SMEBusinessID) {
            await this.getSMEPersonsByBusinessID(this.smeContract.SMEBusinessID);
          }
          if (this.smeContract.startDate) {
            this.smeContract.startDate = moment(this.smeContract.startDate).format('YYYY-MM-DD');
          }
          if (this.smeContract.endDate) {
            this.smeContract.endDate = moment(this.smeContract.endDate).format('YYYY-MM-DD');
          }
          this.addFarmsiteForm.get('SMEDateRangeForm').patchValue({
            start: this.smeContract.startDate ? new Date(this.smeContract.startDate) : null,
            end: this.smeContract.endDate ? new Date(this.smeContract.endDate) : null
          });
          this.smeOnDateChanged({ startDate: new Date(this.smeContract.startDate), endDate: new Date(this.smeContract.endDate) });
        }
      }
      // BuyerContract
      if (this.isBuyerChecked) {
        if (this.farmSite && this.farmSite.buyerContract.length > 0) {
          this.buyerContract = this.farmSite.buyerContract[0];
          if (this.buyerContract.buyerBusinessID) {
            await this.getBuyerPersonsByBusinessID(this.buyerContract.buyerBusinessID);
          }
          if (this.buyerContract.startDate) {
            this.buyerContract.startDate = moment(this.buyerContract.startDate).format('YYYY-MM-DD');
          }
          if (this.buyerContract.endDate) {
            this.buyerContract.endDate = moment(this.buyerContract.endDate).format('YYYY-MM-DD');
          }
          this.addFarmsiteForm.get('BuyerDateRangeForm').patchValue({
            start: this.buyerContract.startDate ? new Date(this.buyerContract.startDate) : null,
            end: this.buyerContract.endDate ? new Date(this.buyerContract.endDate) : null
          });
          this.buyerOnDateChanged({ startDate: new Date(this.buyerContract.startDate), endDate: new Date(this.buyerContract.endDate) });
        }
      }
      if (this.farmSite.ETSOnPremises == true) {
        this.farmSite.ETSOnPremises = true;
      } else {
        this.farmSite.ETSOnPremises = false;
      }
      if (this.farmSite.labOnPremises == true) {
        this.farmSite.labOnPremises = true;
      } else {
        this.farmSite.labOnPremises = false;
      }
      if (this.farmSite.bioSecurity) {
        if (_.find(this.farmSite.bioSecurity, {'name': 'Bird Fencing'})) {
          this.bioBirdFencing = true;
        }
        if (_.find(this.farmSite.bioSecurity, {'name': 'Fencing'})) {
          this.bioFencing = true;
        }
        if (_.find(this.farmSite.bioSecurity, {'name': 'Hand Wash and Footbath Area'})) {
          this.bioHWandFA = true;
        }
        if (_.find(this.farmSite.bioSecurity, {'name': 'Crab Fencing'})) {
          this.bioCrabFencing = true;
        }
        if (_.find(this.farmSite.bioSecurity, {'name': 'Reservoirs'})) {
          this.bioReservoirs = true;
        }
        if (_.find(this.farmSite.bioSecurity, {'name': 'Filters'})) {
          this.bioFilters = true;
        }
      }
    } catch (error) {
      this.toasterService.error(error?.error?.message || error?.message || error);
    }
  }

  /**
   * @method getAllFarmProvider
   * @async
   * @description Fetches all farm provider businesses.
   */
  public async getAllFarmProvider() {
    let result = this.businessTypes.filter(obj => {
      return obj.name === 'Feed Provider' || obj.name === 'Seed Provider';
    });
    let providerIds = [];
    result.forEach(function(object) {
      providerIds.push(object.id);
    });
    try {
      this.businessEntities = await this.businessEntityService.getProviderBusiness(providerIds);
    } catch (error) {
      this.toasterService.error(error?.error?.message || error?.message || error);
    }
  }

  /**
   * @method getSmeBusiness
   * @async
   * @description Fetches all SME businesses.
   */
  public async getSmeBusiness() {
    let smeresult = this.businessTypes.filter(obj => {
      return obj.name === 'SME';
    });
    let smeIds = [];
    smeresult.forEach(function(object) {
      smeIds.push(object.id);
    });
    try {
      this.smeBusinessEntities = await this.businessEntityService.getProviderBusiness(smeIds);
    } catch (error) {
      this.toasterService.error(error?.error?.message || error?.message || error);
    }
  }

  /**
   * @method getProviderBusiness
   * @async
   * @description Fetches all provider businesses (Feed/Seed).
   */
  public async getProviderBusiness() {
    let feedSeedresult = this.businessTypes.filter(obj => {
      return obj.name === 'Seed Provider' || obj.name === 'Feed Provider';
    });
    let feedSeedIds = [];
    feedSeedresult.forEach(function(object) {
      feedSeedIds.push(object.id);
    });
    try {
      this.providerBusinessEntities = await this.businessEntityService.getProviderBusiness(feedSeedIds);
    } catch (error) {
      this.toasterService.error(error?.error?.message || error?.message || error);
    }
  }

  /**
   * @method getBuyerBusiness
   * @async
   * @description Fetches all buyer businesses.
   */
  public async getBuyerBusiness() {
    let buyerresult = this.businessTypes.filter(obj => {
      return obj.name === 'Buyer';
    });
    let buyerIds = [];
    buyerresult.forEach(function(object) {
      buyerIds.push(object.id);
    });
    try {
      this.buyerBusinessEntities = await this.businessEntityService.getProviderBusiness(buyerIds);
    } catch (error) {
      this.toasterService.error(error?.error?.message || error?.message || error);
    }
  }

  /**
   * @method closeForm
   * @description Resets the form and component state to default.
   */
  public closeForm() {
    this.addFarmsiteForm.reset();
    this.showForm = false;
    this.farmSite = new FarmSiteModel();
    this.address = new Address();
    this.inIt();
    this.villageChanged = false;
    this.waterareavalidation = false;
    this.submitted = false;
    this.isSMEChecked = false;
    this.isBuyerChecked = false;
    this.isProviderSelected = false;
    this.isSmeSElected = false;
    this.farmSite.contract = [];
    this.persons = [];
    this.farmSite.SMEContract = [];
    this.smeContract.SMEPersonID = '';
    this.smeContract.SMEBusinessID = '';
    this.smeContract.startDate = undefined;
    this.smeContract.endDate = undefined;
    this.farmSite.buyerContract = [];
    this.buyerContract.buyerPersonID = '';
    this.buyerContract.buyerBusinessID = '';
    this.buyerContract.startDate = '';
    this.buyerContract.endDate = '';
    this.address.pincode = undefined;
    this.address.village = '';
    this.address.taluk = '';
    this.address.mandal = '';
    this.address.district = '';
    this.address.state = '';
    this.waterType.waterTypeId = undefined;
    this.soilType.soilTypeId = undefined;
    this.bioFencing = undefined;
    this.bioHWandFA = undefined;
    this.bioCrabFencing = undefined;
    this.bioReservoirs = undefined;
    this.bioBirdFencing = undefined;
    this.bioFilters = undefined;
    this.farmSite.bioSecurity = undefined;
    this.item = undefined;
    this.countrySearch = undefined;
    this.stateSearch = undefined;
    this.villageSearch = undefined;
    this.stateitem = undefined;
    this.villageItem = undefined;
    this.countrySearch = 'India';
    this.country = 'India';
    this.getStates();
    this.filteredVillagesList = new Array();
    this.filteredStatesList = new Array();
  }

  /**
   * @method closeAllDropdowns
   * @description Closes all custom dropdowns.
   */
  public closeAllDropdowns() {
  this.isCountryDropdown = false;
  this.isStateDropdown = false;
  this.isVillageDropdown = false;
}

/**
 * @method toggleDropdown
 * @description Toggles the visibility of a specific dropdown.
 * @param {'country' | 'state' | 'village'} type - The dropdown type.
 */
public toggleDropdown(type: 'country' | 'state' | 'village') {
  this.isCountryDropdown = type === 'country';
  this.isStateDropdown = type === 'state';
  this.isVillageDropdown = type === 'village';
}

  /**
   * @method openForm
   * @description Opens the form for adding a new farm site.
   * Sets default values (e.g., Country = India).
   */
  public openForm() {
    this.showForm = true;
    this.submitButton = true;
    this.updateButton = false;
    this.country = 'India',
    this.addFarmsiteForm.get('countryName')!.setValue(this.country);
    this.countrySearch = this.country;
    this.isCountryChanged = true;
    this.getStates();
    this.isSMEChecked = false;
    this.providerOrSme = null;
  }

  /**
   * @method enableDisableProviderOrSME
   * @description Resets Provider/SME selection state.
   */
  public enableDisableProviderOrSME() {
    this.providerOrSme = null
    this.isProviderSelected = false;
    this.isSmeSElected = false;
    this.smeContract.SMEBusinessID = '';
    this.smeContract.SMEPersonID = '';
    this.smeContract.startDate = '';
    this.smeContract.endDate = '';
    this.farmSite.SMEContract = [];
    this.farmSite.chatUserConfig = '';
    this.contract.feedProviderID = '';
    this.contract.feedProviderPersonID = '';
    this.farmSite.contract = [];
    this.farmSite.providerUserConfig = [];
    this.clearSMEDateRange();
  }

  /**
   * @method enableDisableBuyer
   * @description Resets Buyer selection state.
   */
  public enableDisableBuyer() {
    this.buyerContract.buyerBusinessID = '';
    this.buyerContract.buyerPersonID = '';
    this.farmSite.buyerUserConfig = '';
    this.clearBuyerDateRange();
  }

  /**
   * @method getpondDetailsByFarmsiteId
   * @async
   * @description Fetches pond details for a farm site to handle deletion.
   * @param {string} farmsiteid - The farm site ID.
   */
  public async getpondDetailsByFarmsiteId(farmsiteid: string) {
    try {
      // $('#myModal').modal('show');
      ($('#myModal') as any).modal({
        backdrop: 'static',
        keyboard: false,
        show: true
      });
      this.pgLoad = false;
      this.customerPonds = await this.customerPondService.getAllPondDetailsByFarmSiteId(farmsiteid);
      if (this.customerPonds.length > 0) {
        for (let i = 0; i < this.customerPonds.length; i++) {
          let pondids = this.customerPonds[i];
          this.pondArr.push(pondids.id);
        }
        this.getPondRelatedData(this.pondArr);
        this.pgLoad = true;
      } else {
        this.toasterService.warning('Data not available');
        $('#myModal').modal('hide');
        this.pgLoad = true;
      }
    } catch (error) {
      this.toasterService.error(error?.error?.message || error?.message || error);
    }
  }

  /**
   * @method getPondRelatedData
   * @async
   * @description Fetches related data (images, etc.) for a list of ponds.
   * @param {Array<string>} pondId - List of pond IDs.
   */
  public async getPondRelatedData(pondId: Array<string>) {
    try {
      this.imageDetails = await this.imageDetailService.getPonds(pondId);
      const keys = Object.keys(this.imageDetails[0]);
      const values = Object.values(this.imageDetails[0]);
      let text: string = '';
      for (let i = 0; i < keys.length; i++) {
        text += keys[i] + '-' + values[i] + '\n';
      }
      this.pgLoad = true;
    } catch (error) {
      this.toasterService.error(error?.error?.message || error?.message || error);
    }
  }

  /**
   * @method deletePond
   * @async
   * @description Deletes pond data based on selected type (Data only or Data + Pond).
   */
  public async deletePond() {
    try {
      if (!this.selectedDeletedataType) {
        this.toasterService.error('Please select "Delete pond(s) data" or "Delete pond data with ponds"');
        return false;
      }

      // No need to flip the value manually
      let pond_delete = this.selectedDeletedataType;  

      this.imageDetails = await this.imageDetailService.deletePond(this.pondArr, pond_delete);

      this.toasterService.success(
        `${this.selectedDeletedataType === 'True' ? 'Pond data with ponds' : 'Ponds data'} deleted successfully`, 
        '', 
        { timeOut: 2000 }
      );

      this.pondArr = [];
      $('#myModal').modal('hide');
      this.selectedDeletedataType = '';
    } catch (error) {
      this.toasterService.error(error?.error?.message || error?.message || error);
    }
  }

  /**
   * @method cancelData
   * @description Resets deletion selection.
   */
  public cancelData() {
    this.selectedDeletedataType = undefined;
    this.pondArr = [];
  }

  /**
   * @method cancel
   * @description Handles cancellation of form or modal.
   */
  public cancel() {
    if ((this.loginPersonType == 'Business Admin' || this.loginPersonType == 'Provider Manager') && this.loginUserBusinessCategory == 'Provider') {
      this.close();
    } else {
      this.closeForm();
    }
  }

  /**
   * @method close
   * @description Closes the add farmsite modal and navigates back.
   */
  public close() {
    $('#addFarmsiteModal').modal('hide');
    this.router.navigate(['/home/BusinessFP-Farm-Farmsite', this.loginBusinessID, this.navparamBusinessID, this.paramsBusiness]);
  }

  /**
   * @method decimalFilter
   * @description Validates input to accept only two digits after decimal point.
   * @param {any} event - The keyboard event.
   */
  public decimalFilter(event: any) {
    this.growOutPondsValidation = false;
    const reg = /^-?\d*(\.\d{0,2})?$/;
    let input = event.target.value + String.fromCharCode(event.charCode);
    if (!reg.test(input)) {
      this.growOutPondsValidation = true;
      event.preventDefault();
    }
  }

  /**
   * @method filterCountries
   * @description Filters the list of countries based on user input.
   * @side-effect Updates `filteredCountriesList`.
   */
  public filterCountries(): void {
    const value = (this.addFarmsiteForm.get('countryName')!.value || '') as CountriesModel | string;
       const searchText =
         typeof value === 'string' ? value.toLowerCase() : value.name.toLowerCase();

       this.filteredCountriesList = this.countries.filter(o =>
         o.name.toLowerCase().includes(searchText)
       );
  }

  /**
   * @method coutrySelection
   * @description Handles country selection. Updates form and fetches states.
   * @param {any} country - The selected country object.
   */
  public coutrySelection(country: any): void {
    this.addFarmsiteForm.get('countryName')!.setValue(country.name);
    this.isCountryDropdown = false;
    this.countrySearch = country.name;
    this.country = country.name;
    this.isCountryChanged = true;
    this.getStates();
  }

  /**
   * @method filterStates
   * @description Filters the list of states based on user input.
   * @side-effect Updates `filteredStatesList`.
   */
  public filterStates(): void {
    const value = (this.addFarmsiteForm.get('stateName')!.value || '') as StateModel | string;
    const searchText =
      typeof value === 'string' ? value.toLowerCase() : value.name.toLowerCase();

    this.filteredStatesList = this.states.filter(o =>
      o.name.toLowerCase().includes(searchText)
    );
  }

  /**
   * @method stateSelection
   * @description Handles state selection. Updates form and fetches villages.
   * @param {any} state - The selected state object.
   */
  public stateSelection(state: any): void {
    this.addFarmsiteForm.get('stateName')!.setValue(state.name);
    this.address.state = state.name;
    this.stateSearch = state.name;
    this.isStateDropdown = false;
    this.getVillages(state);
  }

  /**
   * @method getStates
   * @description Retrieves and populates the list of states based on the selected country.
   */
  public getStates() {
    this.states = [];
    this.villages = [];
    this.filteredStatesList = [];
    this.filteredVillagesList = [];
    if (this.submitButton || (this.updateButton && this.isCountryChanged)) {
      this.addFarmsiteForm.get('stateName')!.setValue('');
      this.addFarmsiteForm.get('villageName')!.setValue('');
    }
    const selectedCountryObject = this.countries.find((country: CountriesModel) => country.name == this.country);
    this.states = selectedCountryObject.states;
    this.filteredStatesList = this.states;
    this.stateVillageDisabled = false;
  }

  /**
    * @method getVillages
    * @async
    * @description Retrieves and populates the list of villages based on the selected state.
    */
  public isLoadingVillages = false;
  public isLoadingMore = false;
  public villagesLoadError = false;
  public visibleVillages: any[] = [];
  public CHUNK_SIZE = 50;
  private currentIndex = 0;
  private searchText = '';
  private isLoadTriggered = false;
  public noDataFound = false;

  public async getVillages(state: any) {
    this.resetVillages();
    this.isLoadingVillages = true;
    this.villagesLoadError = false;
    this.noDataFound = false;

    try {
      const response = await this.statesAPIService.getVillagesByStates(state.name);
      if (response?.[0]?.city_town_village?.length) {
        this.villages = response[0].city_town_village;
        this.loadNextChunk();
      } else {
        this.villagesLoadError = true;
      }
    } catch (err) {
      this.villagesLoadError = true;
    } finally {
      this.isLoadingVillages = false;
    }
  }

  /**
   * @method loadNextChunk
   * @private
   * @description Loads the next chunk of villages for infinite scroll.
   */
  private loadNextChunk() {
    const source = this.searchText
      ? this.villages.filter(v =>
          v['city_town_village_name'].toLowerCase().includes(this.searchText.toLowerCase())
        )
      : this.villages;

    if (source.length === 0) {
      this.noDataFound = true;
      return;
    }

    this.noDataFound = false; // reset when data exists
    const nextSet = source.slice(this.currentIndex, this.currentIndex + this.CHUNK_SIZE);
    this.visibleVillages = [...this.visibleVillages, ...nextSet];
    this.currentIndex += this.CHUNK_SIZE;
  }

  /**
   * @method onVillageScroll
   * @description Handles scroll event on village dropdown to load more items.
   */
  public onVillageScroll() {
    if (this.isLoadTriggered || this.isLoadingMore) return;

    const remaining = this.getFilteredList()?.length - this.visibleVillages.length;
    if (remaining > 0) {
      this.isLoadTriggered = true;
      this.isLoadingMore = true;

      setTimeout(() => {
        this.ngZone.run(() => {
          this.loadNextChunk();
          this.isLoadingMore = false;
          this.isLoadTriggered = false;
          this.cdr.detectChanges();
        });
      }, 300);
    }
  }

  /**
   * @method filterVillages
   * @description Filters the village list based on user input.
   * @param {any} event - The input event.
   */
  public filterVillages(event: any) {
    this.searchText = event.target.value;
    this.visibleVillages = [];
    this.currentIndex = 0;
    this.loadNextChunk();
  }

  /**
   * @method getFilteredList
   * @private
   * @description Helper to get filtered list of villages.
   */
  private getFilteredList() {
    return this.searchText
      ? this.villages.filter(v =>
          v['city_town_village_name'].toLowerCase().includes(this.searchText.toLowerCase())
        )
      : this.villages;
  }

  /**
   * @method resetVillages
   * @private
   * @description Resets village data structures.
   */
  private resetVillages() {
    this.villages = [];
    this.visibleVillages = [];
    this.currentIndex = 0;
    this.searchText = '';
    this.isLoadTriggered = false;
    this.noDataFound = false;
    this.addFarmsiteForm.get('villageName')?.setValue('');
  }

  /**
   * @method onVillageFocus
   * @description Handles focus event on village input.
   */
  public onVillageFocus() {
    if (!this.searchText) {
      // If no search text, just reload first chunk
      this.visibleVillages = [];
      this.currentIndex = 0;
      this.loadNextChunk();
    } else {
      // If search exists, reload search result
      this.visibleVillages = [];
      this.currentIndex = 0;
      this.loadNextChunk();
    }
  }

  /**
   * @method onVillageBlur
   * @description Handles blur event on village input.
   */
  public onVillageBlur() {
    if (this.noDataFound || this.visibleVillages.length == 0) {
      this.clearVillageField();
    }
  }

  /**
   * @method clearVillageField
   * @private
   * @description Clears the village input field.
   */
  private clearVillageField() {
    this.addFarmsiteForm.get('villageName')?.setValue('');
    this.visibleVillages = [];
    this.currentIndex = 0;
    this.searchText = '';
  }

  /**
   * @method villageSelection
   * @description Handles village selection. Updates form and triggers geocoding.
   * @param {any} village - The selected village object.
   */
  public villageSelection(village: any): void {
    this.addFarmsiteForm.get('villageName')!.setValue(village.city_town_village_name);
    this.isVillageDropdown = false;
    this.villageSearch = village.city_town_village_name;
    this.address.village = this.villageSearch;
    if (this.address.village) {
      this.villageChanged = true;
      const villageParts = this.address.village.toLowerCase().split(',');
      const district = villageParts.length > 1 ? villageParts[1] : '';
      const state = villageParts.length > 2 ? villageParts[2] : this.address.state;
      this.checkPincode(this.address.village, this.address.state, district, state);
    }
  }

  /**
   * @method navigateToAccountClosurePopupOpenScreen
   * @description Navigates to the account closure screen.
   */
  public navigateToAccountClosurePopupOpenScreen() {
    this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
    this.router.navigate(['/home/account-closure']);
  }

  /**
   * @method NavigateToBusinessEntityScreen
   * @description Navigates to the business entity list screen.
   */
  public NavigateToBusinessEntityScreen(){
    this.router.navigate(['/home/Business-Entity/Business'])

  }
}
